import {
  calendarDayOfWeek,
  diffCalendarDays,
  timeOfDayFromApi,
  toCalendarDay,
} from '@shared/models/dateTypes';
import { WEEKDAYS_SHORT } from '@shared/components/abook-school-calendar/school-calendar.utils';
import { REPEAT_INTERVALS } from '@features/booking/calender/templates/create-booking-modal/components/repeated-booking/repeat-days.util';
import { CombinedBasketItem } from './basket';

/** One card in the basket: a single line, or a repeat's lines (#1138). */
export interface BasketSeries {
  key: string;
  /** In date order. */
  items: CombinedBasketItem[];
  /** The lines' prices together. */
  price: number;
  /** Some date in it cannot be booked. */
  unavailable: boolean;
  /** How it repeats, for a series' header ({@link seriesRhythm}); empty for a single line. */
  rhythm: string;
}

/** The weekly intervals by their step in days, named as the Gentag chip names them. */
const RHYTHM_BY_STEP = new Map<number, string>(
  (
    [
      [7, 'w1'],
      [14, 'w2'],
      [21, 'w3'],
    ] as const
  ).map(([step, value]) => [
    step,
    REPEAT_INTERVALS.find((option) => option.value === value)?.label ?? '',
  ]),
);

/**
 * How a series repeats, for its card's header (#1138): "Hver uge · tir", "Hver 2. uge · man, tor" —
 * the rhythm when the dates keep one, then the weekdays from Monday. A series with a date taken out
 * keeps no rhythm and says only its weekdays.
 */
export function seriesRhythm(items: readonly CombinedBasketItem[]): string {
  const days = [...new Set(items.map((item) => toCalendarDay(item.start)))].sort();
  const weekdays = [...new Set(days.map(calendarDayOfWeek))].sort(
    (a, b) => ((a + 6) % 7) - ((b + 6) % 7),
  );
  const names = weekdays.map((d) => WEEKDAYS_SHORT[d]).join(', ');
  // One step from each date to the same weekday's next: the series' rhythm, if they all agree.
  const steps = new Set<number>();
  for (const weekday of weekdays) {
    const same = days.filter((day) => calendarDayOfWeek(day) === weekday);
    for (let i = 1; i < same.length; i++) steps.add(diffCalendarDays(same[i], same[i - 1]));
  }
  const step = steps.size === 1 ? [...steps][0] : 0;
  const rhythm = RHYTHM_BY_STEP.get(step) ?? '';
  return [rhythm, names].filter(Boolean).join(' · ');
}

/**
 * The basket's lines grouped into series (#1138): lines on the same activity, facilitet, time of day
 * and availability type, each within one day, are a repeat and share a card; a line over midnight
 * or without a match stands alone. In the order the lines first appear.
 */
export function basketSeries(items: readonly CombinedBasketItem[]): BasketSeries[] {
  const groups = new Map<string, CombinedBasketItem[]>();
  for (const item of items) {
    const sameDay = toCalendarDay(item.start) === toCalendarDay(item.end);
    const key = sameDay
      ? [
          item.activityId,
          item.resourceId,
          timeOfDayFromApi(item.start),
          timeOfDayFromApi(item.end),
          item.availabilityType,
        ].join('|')
      : `single|${item.id}`;
    const group = groups.get(key);
    if (group) group.push(item);
    else groups.set(key, [item]);
  }
  return [...groups].map(([key, group]) => {
    const sorted = [...group].sort((a, b) => a.start.localeCompare(b.start));
    return {
      key,
      items: sorted,
      price: sorted.reduce((sum, item) => sum + (item.price ?? 0), 0),
      unavailable: sorted.some((item) => !item.isAvailable),
      rhythm: sorted.length > 1 ? seriesRhythm(sorted) : '',
    };
  });
}
